import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Check, Loader2, Zap, Sparkles, CreditCard, ArrowRight } from 'lucide-react';

const QUICK_USD = [10, 20, 50, 100];

const fmtPrice = (usd) => {
  if (usd === 0) return '$0';
  if (usd == null) return 'Custom';
  return `$${usd.toLocaleString()}`;
};

/**
 * Global one-click upgrade drawer: top up bill credits OR pick a plan, in a
 * single surface. Opened app-wide via a `billportal:open-upgrade` window event
 * (detail: { tab?: 'credits'|'plans', accountId? }). Mounted once in MainLayout.
 */
export default function UpgradeSheet() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('credits');
  const [accountId, setAccountId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [busy, setBusy] = useState(null);
  const [customUsd, setCustomUsd] = useState('');

  useEffect(() => {
    const onOpen = (e) => {
      const d = e.detail || {};
      setTab(d.tab === 'plans' ? 'plans' : 'credits');
      setAccountId(d.accountId || getCurrentAccount()?.id || null);
      setOpen(true);
    };
    window.addEventListener('billportal:open-upgrade', onOpen);
    return () => window.removeEventListener('billportal:open-upgrade', onOpen);
  }, []);

  const loadPlans = useCallback(async () => {
    if (plans.length) return;
    setLoadingPlans(true);
    try {
      const { data } = await api.get('/subscription/plans');
      setPlans((data?.plans || []).filter((p) => p.id !== 'free'));
    } catch {
      toast.error('Could not load plans');
    } finally {
      setLoadingPlans(false);
    }
  }, [plans.length]);

  useEffect(() => {
    if (open && tab === 'plans') loadPlans();
  }, [open, tab, loadPlans]);

  const buyCredits = async (amountUsd) => {
    const usd = Number(amountUsd);
    if (!usd || usd < 5) { toast.error('Minimum top-up is $5'); return; }
    setBusy(`credits-${amountUsd}`);
    try {
      const { data } = await api.post(
        '/billing/bill-credits/checkout',
        { amount_usd: usd, origin_url: window.location.origin },
        { params: { account_id: accountId } },
      );
      if (data?.url) { window.location.href = data.url; return; }
      throw new Error('No checkout URL');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setBusy(null);
    }
  };

  const pickPlan = async (plan) => {
    if (plan.cta_url) {
      setOpen(false);
      window.open(plan.cta_url, '_blank', 'noopener');
      return;
    }
    if (plan.id === 'enterprise') {
      setOpen(false);
      window.dispatchEvent(new CustomEvent('ai-agent:open', {
        detail: { tab: 'chats', prompt: "I'm interested in the Enterprise plan. Can you help me get in touch with sales?" },
      }));
      return;
    }
    setBusy(`plan-${plan.id}`);
    try {
      const { data } = await api.post('/subscription/checkout', {
        plan_id: plan.id,
        origin_url: window.location.origin,
        account_id: accountId,
      });
      if (data?.url) { window.location.href = data.url; return; }
      throw new Error('No checkout URL');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setBusy(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="upgrade-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2 text-lg font-black tracking-tight">
            <Sparkles className="h-4 w-4 text-primary" /> Keep processing bills
          </SheetTitle>
          <SheetDescription className="text-[13px]">
            Top up bill credits instantly, or move to a plan for a monthly allowance and parallel processing.
          </SheetDescription>
        </SheetHeader>

        {/* Tab switch */}
        <div className="mt-4 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1" data-testid="upgrade-sheet-tabs">
          <button
            type="button"
            onClick={() => setTab('credits')}
            data-testid="upgrade-tab-credits"
            className={`flex items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${tab === 'credits' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Zap className="h-3.5 w-3.5" /> Buy credits
          </button>
          <button
            type="button"
            onClick={() => setTab('plans')}
            data-testid="upgrade-tab-plans"
            className={`flex items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${tab === 'plans' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <CreditCard className="h-3.5 w-3.5" /> Upgrade plan
          </button>
        </div>

        {tab === 'credits' ? (
          <div className="mt-5 space-y-4" data-testid="upgrade-credits-panel">
            <p className="text-[13px] text-slate-500 leading-relaxed">
              1 bill credit = one account on a parsed bill. Purchased credits <span className="font-semibold text-slate-700">never expire</span> and roll over each month.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_USD.map((usd) => (
                <button
                  key={usd}
                  type="button"
                  disabled={!!busy}
                  onClick={() => buyCredits(usd)}
                  data-testid={`upgrade-credit-${usd}`}
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-900 transition-all hover:border-primary hover:bg-primary/5 disabled:opacity-50"
                >
                  {busy === `credits-${usd}` ? <Loader2 className="h-4 w-4 animate-spin" /> : `$${usd}`}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                <input
                  type="number"
                  min={5}
                  value={customUsd}
                  onChange={(e) => setCustomUsd(e.target.value)}
                  placeholder="Custom amount"
                  data-testid="upgrade-credit-custom-input"
                  className="w-full rounded-lg border border-slate-200 py-2.5 pl-7 pr-3 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <Button
                onClick={() => buyCredits(customUsd)}
                disabled={!!busy || !customUsd}
                data-testid="upgrade-credit-custom-buy"
                className="shrink-0 rounded-lg font-semibold"
              >
                {busy === `credits-${customUsd}` ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buy'}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400">Minimum top-up $5. Secure checkout via Stripe.</p>
          </div>
        ) : (
          <div className="mt-5 space-y-3" data-testid="upgrade-plans-panel">
            {loadingPlans ? (
              <div className="flex items-center justify-center py-16 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading plans…
              </div>
            ) : (
              plans.map((plan) => {
                const highlight = plan.id === 'starter';
                return (
                  <div
                    key={plan.id}
                    data-testid={`upgrade-plan-${plan.id}`}
                    className={`rounded-xl border p-4 ${highlight ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-black tracking-tight">{plan.label}</span>
                          {highlight && <span className="rounded-full bg-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">Popular</span>}
                        </div>
                        <div className="mt-1 text-lg font-black tracking-tighter">
                          {fmtPrice(plan.usd_per_month)}
                          {plan.usd_per_month != null && plan.usd_per_month > 0 && <span className="text-xs font-medium text-slate-400">/mo</span>}
                        </div>
                        {plan.tagline && <p className="mt-1 text-[12px] leading-snug text-slate-500">{plan.tagline}</p>}
                      </div>
                    </div>
                    {(plan.bullets || []).length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {(plan.bullets || []).slice(0, 3).map((b, i) => (
                          <li key={i} className="flex items-start gap-2 text-[12px] leading-snug text-slate-600">
                            <Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-primary" /><span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      onClick={() => pickPlan(plan)}
                      disabled={!!busy}
                      data-testid={`upgrade-plan-cta-${plan.id}`}
                      className="mt-3 h-10 w-full rounded-lg font-bold uppercase tracking-wide text-xs"
                      variant={highlight ? 'default' : 'outline'}
                    >
                      {busy === `plan-${plan.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                        <>{plan.cta_url ? 'Contact us' : plan.id === 'enterprise' ? 'Talk to sales' : `Choose ${plan.label}`} <ArrowRight className="h-3.5 w-3.5 ml-1" /></>
                      )}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function openUpgradeSheet(tab = 'credits', accountId = null) {
  window.dispatchEvent(new CustomEvent('billportal:open-upgrade', { detail: { tab, accountId } }));
}
